import { isFonemaWhatsappRuntimeConfigured } from '../fonema/infrastructure/config/fonemaEnvConfig';
import { isVoicePlatformRuntimeConfigured } from '../voice/infrastructure/config/voiceEnvConfig';

/** @typedef {'fonema'|'voice'|null} CallProvider */

/**
 * Rollout/entitlement: el módulo de llamadas está disponible para el retailer.
 * No implica que Voice tenga SIP registrado.
 *
 * @param {Object|null|undefined} retailerInfo
 * @returns {boolean}
 */
export function isWhatsappCallsAvailableForRetailer(retailerInfo) {
  if (retailerInfo?.whatsapp_calls_available != null) {
    return Boolean(retailerInfo.whatsapp_calls_available);
  }

  if (retailerInfo?.whatsapp_calls_feature_enabled != null) {
    return Boolean(retailerInfo.whatsapp_calls_feature_enabled);
  }

  return Boolean(
    retailerInfo?.voice_platform_calls_enabled
    || retailerInfo?.fonema_whatsapp_calls_enabled
  );
}

/**
 * Estado real de conexión: Fonema habilitado o Voice con al menos una línea SIP.
 * Es el gate para montar runtime, consultar SIP y habilitar historial/acciones.
 *
 * @param {Object|null|undefined} retailerInfo
 * @returns {boolean}
 */
export function isWhatsappCallsConnectedForRetailer(retailerInfo) {
  if (retailerInfo?.whatsapp_calls_connected != null) {
    return Boolean(retailerInfo.whatsapp_calls_connected);
  }

  if (retailerInfo?.voice_platform_calls_connected != null) {
    return Boolean(retailerInfo.voice_platform_calls_connected)
      || Boolean(retailerInfo?.fonema_whatsapp_calls_enabled);
  }

  // Payload legacy sin campos de conexión: mejor esfuerzo con el flag anterior.
  return Boolean(retailerInfo?.whatsapp_calls_feature_enabled);
}

/**
 * @param {Object|null|undefined} retailerInfo
 * @returns {boolean}
 */
export function isWhatsappCallsFeatureEnabledForRetailer(retailerInfo) {
  return isWhatsappCallsAvailableForRetailer(retailerInfo);
}

/**
 * @param {Object|null|undefined} retailerInfo
 * @returns {CallProvider}
 */
export function resolveCallProvider(retailerInfo) {
  if (!isWhatsappCallsConnectedForRetailer(retailerInfo)) {
    return null;
  }

  const voiceConnected = retailerInfo?.voice_platform_calls_connected != null
    ? Boolean(retailerInfo.voice_platform_calls_connected)
    : Boolean(retailerInfo?.voice_platform_calls_enabled);
  const voiceConfigured = isVoicePlatformRuntimeConfigured();

  if (voiceConnected && voiceConfigured) {
    return 'voice';
  }

  const fonemaFlagEnabled = Boolean(retailerInfo?.fonema_whatsapp_calls_enabled);
  const fonemaConfigured = isFonemaWhatsappRuntimeConfigured();

  if (fonemaFlagEnabled && fonemaConfigured) {
    return 'fonema';
  }

  return null;
}

/**
 * @param {CallProvider} provider
 * @returns {boolean}
 */
export function isWhatsappCallsUiEnabledForProvider(provider) {
  return provider === 'fonema' || provider === 'voice';
}
